import { Field, Form, Formik } from 'formik';
import { isNumber } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '../../../../../../CommonComponents/Button';
import FormikMaskedInput from '../../../../../../CommonComponents/FormikMaskedInput';
import { percentageMask } from '../../../../../../CommonComponents/MaskedInput';
import { AssetClass, Returns, Volatilities } from '../../../types';
import { BottomNavigation, SectionTitle, StepBody, StepContent } from '../styles';

const AssetClassName = styled.div`
  white-space: nowrap;
`;

const Table = styled.table`
  thead {
    color: #0078d4;

    th {
      padding-bottom: 16px;
      font-weight: 600;
      text-align: left;
    }

    th + th {
      padding-left: 32px;
    }
  }

  tbody {
    td + td {
      padding-left: 32px;
    }
    tr + tr {
      td {
        padding-top: 16px;
      }
    }
  }
`;

interface EditCapitalMarketAssumptionsProps {
  assetClasses: AssetClass[];
  returns: Returns;
  volatilities: Volatilities;
  next: (updatedReturns: Returns, updatedVolatilities: Volatilities) => void;
}

export const EditCapitalMarketAssumptions: React.FC<EditCapitalMarketAssumptionsProps> = ({
  assetClasses,
  returns,
  volatilities,
  next,
}) => {
  const initialValues: { returns: Returns; volatilities: Volatilities } = {
    returns: Object.entries(returns).reduce(
      (acc, [assetClassId, value]) => ({ ...acc, [assetClassId]: value * 100 }),
      {},
    ),
    volatilities: Object.entries(volatilities).reduce(
      (acc, [assetClassId, value]) => ({ ...acc, [assetClassId]: value * 100 }),
      {},
    ),
  };

  return (
    <StepBody>
      <Formik
        onSubmit={({ returns: updatedReturns, volatilities: updatedVolatilities }) => {
          next(
            Object.entries(updatedReturns).reduce(
              (acc, [assetClassId, value]) => ({ ...acc, [assetClassId]: value / 100 }),
              {},
            ),
            Object.entries(updatedVolatilities).reduce(
              (acc, [assetClassId, value]) => ({ ...acc, [assetClassId]: value / 100 }),
              {},
            ),
          );
        }}
        initialValues={initialValues}
        isInitialValid={assetClasses
          .filter(({ included }) => included)
          .every(
            ({ id }) =>
              isNumber(initialValues.returns[id]) && isNumber(initialValues.volatilities[id]),
          )}
        render={({ isValid }) => (
          <Form>
            <StepContent>
              <div>
                <SectionTitle>Returns and Volatilities</SectionTitle>

                <Table>
                  <thead>
                    <tr>
                      <th>ASSET CLASS</th>
                      <th>AVG RETURN</th>
                      <th>VOLATILITY</th>
                    </tr>
                  </thead>

                  <tbody>
                    {assetClasses
                      .filter(({ included }) => included)
                      .map(({ id, customName }) => (
                        <tr key={id}>
                          <td>
                            <AssetClassName>{customName}</AssetClassName>
                          </td>
                          <td>
                            <Field
                              component={FormikMaskedInput}
                              mask={percentageMask}
                              appendIcon="fa fa-percent"
                              validate={v => (v !== '' ? undefined : 'Required')}
                              name={`returns[${id}]`}
                            />
                          </td>
                          <td>
                            <Field
                              component={FormikMaskedInput}
                              mask={percentageMask}
                              appendIcon="fa fa-percent"
                              validate={v => (v !== '' ? undefined : 'Required')}
                              name={`volatilities[${id}]`}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </StepContent>
            <BottomNavigation>
              <PrimaryButton type="submit" disabled={!isValid}>
                Next
              </PrimaryButton>
            </BottomNavigation>
          </Form>
        )}
      />
    </StepBody>
  );
};
